import { buildCollection, type AuthController } from "@firecms/core";
import { rolesOf } from "../roles";
import { CURRENCIES, currencyMapProperty } from "./money";
import { ShippingFeesPreview, ShippingRatesPreview } from "./shipping-fees";

/** 設定類：員工可看，只有管理員能改 */
function adminPermissions({ authController }: { authController: AuthController }) {
  const r = rolesOf(authController);
  return { read: r.staff, edit: r.admin, create: r.admin, delete: r.admin };
}

export const currenciesCollection = buildCollection({
  id: "currencies",
  path: "currencies",
  name: "貨幣",
  singularName: "貨幣",
  group: "設定",
  icon: "payments",
  // 文件 ID 是貨幣代碼；網站只認得 lib/core/shop-currencies.ts 的 shopCurrencyIds
  customId: CURRENCIES,
  description: "顧客在網站上選的貨幣：決定計價的幣別與線上金流，不限制寄送地區或商品。",
  permissions: adminPermissions,
  initialSort: ["sort", "asc"],
  properties: {
    paymentProvider: { name: "金流", dataType: "string", enumValues: { ecpay: "綠界", paypal: "PayPal" }, validation: { required: true } },
    sort: { name: "排序", dataType: "number" },
  },
});

export const shippingZonesCollection = buildCollection({
  id: "shippingZones",
  path: "shippingZones",
  name: "運費",
  singularName: "運費區域",
  group: "設定",
  icon: "local_shipping",
  customId: true,
  description: "依收件國家、重量分級的運費，和顧客選的貨幣無關。結帳時用第一個「最大重量 ≥ 包裹重量」的級距；每個貨幣都要填運費，沒填的貨幣寄到這一區時不能下單。",
  permissions: adminPermissions,
  initialSort: ["sort", "asc"],
  properties: {
    carrier: { name: "承運商", dataType: "string", enumValues: { tcat: "黑貓", sf: "順豐", ems: "EMS" }, validation: { required: true } },
    countries: { name: "國家", description: "ISO 代碼；\"*\" 代表其他所有國家", dataType: "array", of: { dataType: "string" } },
    rates: {
      name: "費率",
      description: "依重量級距計費；超過最高級距的訂單沿用最高級距的費率，不會因重量無法下單。",
      dataType: "array",
      columnWidth: 360,
      Preview: ShippingRatesPreview,
      of: {
        dataType: "map",
        properties: {
          maxGrams: { name: "最大重量（公克）", dataType: "number", validation: { required: true, integer: true } },
          fees: { ...currencyMapProperty("運費", "各幣別運費（元）"), Preview: ShippingFeesPreview },
        },
      },
    },
    sort: { name: "排序", dataType: "number" },
  },
});

export const discountCodesCollection = buildCollection({
  id: "discountCodes",
  path: "discountCodes",
  name: "折扣碼",
  singularName: "折扣碼",
  group: "設定",
  icon: "sell",
  // 文件 ID 就是折扣碼；網站比對時不分大小寫（lib/server/catalog.ts 讀進來一律轉小寫）
  customId: true,
  description:
    "文件 ID 就是折扣碼，建議用小寫英數字（顧客輸入不分大小寫）。顧客在結帳頁輸入，每張訂單一組，套用新的會取代舊的；" +
    "依顧客結帳的貨幣折抵固定金額，只折商品金額、不折運費；折抵比商品金額多時，商品最少留 1 元（美元 1 分）。沒填金額的貨幣不能用；不再使用時關掉「啟用」即可，已成立的訂單不受影響。",
  permissions: adminPermissions,
  properties: {
    amounts: currencyMapProperty("折抵金額", "各幣別折抵的金額（元）。沒填的貨幣不能用這個折扣碼。"),
    active: { name: "啟用", dataType: "boolean", defaultValue: true },
  },
});
